import React from 'react';
import { useDispatch } from 'react-redux';

import { useSelector } from 'hooks';

import PageBaseProps from 'types/PageBase';
import { KeyStatus } from 'modules/kiosk/types/Kiosk';
import { isReopenKioskPage } from 'modules/router/selectors';
import { ScheduleTypeEnum } from 'modules/auth/types/InitialState';
import { SelfServiceType } from 'modules/selfServices/types/SelfService';

import { finalizeSelfService } from 'modules/selfServices/actions';
import { closeKeysSafe, openKeysSafe } from 'modules/kiosk/actions';

import {
  isClosed, getIsCloseSuccess, hasError as hasErrorSelector, isOpened,
} from 'modules/kiosk/selectors';
import { hasScheduledTime } from 'modules/auth/selectors';
import { getSelectedSelfService } from 'modules/selfServices/selectors';

import Spinner from 'components/ui/Spinner';
import KeysManagementModal from 'components/Keys/PartteamKey/KeysManagementModal';

import KeysError from './KeysError';
import Inactivity from './Inactivity';
import KeysManagementModalContent from './KeysManagementModalContent';
import ProcessingStepModal, { ProcessingStep } from '../../ui/ProcessingStepModal';
import { KEYCHAIN_INSTRUCTIONS_STEP } from '../../KioskLocationId/KeychainInstructions';

const STEPS = {
  [ProcessingStep.KEYCHAIN_INSTRUCTIONS]: KEYCHAIN_INSTRUCTIONS_STEP,
};

const PartteamKey: React.FC<PageBaseProps> = ({ onNext }) => {
  const dispatch = useDispatch();

  const doorsClosed = useSelector(isClosed);
  const doorsOpened = useSelector(isOpened);
  const hasError = useSelector(hasErrorSelector);
  const isCloseSuccess = useSelector(getIsCloseSuccess);
  const isKioskReopen = useSelector(isReopenKioskPage);
  const selfService = useSelector(getSelectedSelfService);
  const isInactive = useSelector((state) => hasScheduledTime(state, ScheduleTypeEnum.DOORS));

  const [isKeychainInstructionModalOpen, setIsKeychainInstructionModalOpen] = React.useState(false);

  const mobilityEnabled = selfService.mobilityEnabled
    && (selfService.type !== SelfServiceType.CHECK_IN || selfService.keyStatus === KeyStatus.IN_SLOT);

  const handleOpenDoor = React.useCallback(() => {
    setIsKeychainInstructionModalOpen(false);
    dispatch(openKeysSafe({ selfServiceId: selfService.id, selfServiceType: selfService.type }));
  }, [dispatch, selfService.id, selfService.type]);

  const handleCloseDoor = React.useCallback(() => {
    dispatch(closeKeysSafe({ selfServiceId: selfService.id, selfServiceType: selfService.type }));
  }, [dispatch, selfService.id, selfService.type]);

  const shouldUseInactivity = !isKioskReopen && doorsOpened;
  const handleInactivity = React.useCallback(() => {
    dispatch(finalizeSelfService('FINAL_INSTRUCTIONS', 0, true));
  }, [dispatch]);

  React.useEffect(() => {
    if (selfService.type !== SelfServiceType.CHECK_OUT) {
      setIsKeychainInstructionModalOpen(true);
    } else {
      handleOpenDoor();
    }
    // This useEffect should only be triggered once when mounting the component
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (isCloseSuccess && !isInactive) {
      onNext?.();
    }
    // We don't want to trigger this effect when `onNext` changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCloseSuccess]);

  return (
    <div className="pt-60 h-full">
      <KeysManagementModal>
        {hasError && <KeysError onOpen={handleOpenDoor} onClose={handleCloseDoor} />}
        {!hasError && selfService && (
          <KeysManagementModalContent {...selfService} mobilityEnabled={mobilityEnabled} onClose={handleCloseDoor} />
        )}
      </KeysManagementModal>
      <ProcessingStepModal
        open={isKeychainInstructionModalOpen}
        config={STEPS}
        step={ProcessingStep.KEYCHAIN_INSTRUCTIONS}
        onClick={handleOpenDoor}
      />
      {doorsClosed && <Spinner className="mt-96 kiosk:!w-36 kiosk:!h-36" />}
      {shouldUseInactivity && <Inactivity onInactive={handleInactivity} />}
    </div>
  );
};

export default PartteamKey;
